import { Capacitor } from '@capacitor/core';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getPublicStartingSoon } from '../../services/circles.service';
import { useActiveCohortSessions } from '../../stores/active-cohort-sessions.store';
import { useAuthStore } from '../../stores/auth.store';
import type { PublicStartingSoonResponse, StartingSoon } from '../../types/startingSoon';
import { HUB_PATH } from './justMissed';
import { cohortPendingJoin, savePendingJoin } from './pendingCircleJoin';
import {
  inNoticeWindow,
  isAutomatedVisitor,
  isPublicNoticePath,
  LEAD_MS,
  nextPollDelayMs,
  SLOW_POLL_MS,
  toPublicNotice,
} from './publicNotice';
import StartingSoonCard, { StartingSoonView, useTabVisible } from './StartingSoonCard';

interface Polled {
  res: PublicStartingSoonResponse;
  /** server_time minus the device clock when it landed (N4, S7). */
  offsetMs: number;
}

/**
 * The circle-starting notice on the public website (docs/plans/
 * CIRCLE-STARTING-NOTICE.md §5, §7, §8), for whoever is on a public page.
 *
 * - Public pages only (not the auth pages). The /app pages have their own
 *   host (CircleStartBanner under ProtectedRoute) and no /app path is a
 *   public notice path, so a page has exactly one host, never both.
 * - Signed in: the SIGNED-IN notice (§5 "A signed-in user on a public page
 *   gets the signed-in path", N3): the shared app-wide feed, so the person's
 *   own eligibility (not a member, the setting on, the daily cap) decides,
 *   the server rows record it, and the quiet zones apply. Never this poll.
 * - Signed out: the public poll below. A touring guest gets neither.
 */
export default function PublicStartingSoonHost() {
  const { pathname } = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isGuest = useAuthStore((s) => s.isGuest);
  const isLoading = useAuthStore((s) => s.isLoading);

  if (isLoading || isGuest || !isPublicNoticePath(pathname)) return null;
  return isAuthenticated ? (
    <SignedInPublicNotice pathname={pathname} />
  ) : (
    <SignedOutPublicNotice pathname={pathname} />
  );
}

/** Signed in on a public page: the in-app notice, fed by the one shared poll. */
function SignedInPublicNotice({ pathname }: { pathname: string }) {
  useActiveCohortSessions(true);
  const navigate = useNavigate();
  // Nothing of higher priority renders on a public page (the start banner
  // lives under /app), so nothing blocks it here. For the same reason nothing
  // here would take them in at T after an hourly signup: the Circles hub's
  // waiting card does (§5's destination for an hourly signup, PendingCircleJoin).
  const toHub = () => {
    void navigate(HUB_PATH, { state: { tab: 'circles' } });
  };
  return <StartingSoonCard blocked={false} pathname={pathname} onSignedUp={toHub} />;
}

/**
 * Signed out:
 * - Client only: it decides to mount in an effect after the SPA rendered, and
 *   never for automation, crawlers or the prerender pass (isAutomatedVisitor),
 *   so the notice is never in prerendered HTML.
 * - Polls GET circles/starting-soon on load, then every 5 min, then every
 *   45 s inside 15 min of starts_at, and ONLY while the tab is visible.
 * - Join goes to /register?circle= or ?hourly= (the pending join, §5).
 */
function SignedOutPublicNotice({ pathname }: { pathname: string }) {
  const tabVisible = useTabVisible();

  const [allowed, setAllowed] = useState(false);
  useEffect(() => {
    setAllowed(!isAutomatedVisitor() && !Capacitor.isNativePlatform());
  }, []);

  const [polled, setPolled] = useState<Polled | null>(null);

  useEffect(() => {
    if (!allowed || !tabVisible) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      let delay = SLOW_POLL_MS;
      try {
        const res = await getPublicStartingSoon();
        if (cancelled) return;
        const serverMs = Date.parse(res.server_time);
        const offsetMs = Number.isFinite(serverMs) ? serverMs - Date.now() : 0;
        setPolled({ res, offsetMs });
        delay = nextPollDelayMs(res.circle, Date.now() + offsetMs);
      } catch {
        /* quiet: try again on the slow cadence */
      }
      if (!cancelled) timer = setTimeout(() => void tick(), delay);
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [allowed, tabVisible]);

  // Wake when the notice's window opens (T-10) between polls.
  const [, setWake] = useState(0);
  const circle = polled?.res.circle ?? null;
  const offsetMs = polled?.offsetMs ?? 0;
  useEffect(() => {
    if (!circle) return;
    const opensIn = Date.parse(circle.starts_at) - LEAD_MS - (Date.now() + offsetMs);
    if (!(opensIn > 0)) return;
    const t = setTimeout(() => setWake((n) => n + 1), opensIn + 50);
    return () => clearTimeout(t);
  }, [circle, offsetMs]);

  const notice = useMemo(
    () => (polled ? toPublicNotice(polled.res.circle, polled.res.server_time) : null),
    [polled],
  );

  if (!allowed) return null;
  const showable = circle && inNoticeWindow(circle, Date.now() + offsetMs) ? notice : null;

  const onRegister = (n: StartingSoon) => {
    // A session's T rides along, so after verification the runner can tell
    // "still open" from "started without you" (§5 table).
    if (circle && 'cohort_id' in circle.join) {
      savePendingJoin(cohortPendingJoin(circle.join.cohort_id, n.starts_at));
    }
  };

  return (
    <StartingSoonView
      notice={showable}
      offsetMs={offsetMs}
      blocked={false}
      pathname={pathname}
      signedIn={false}
      onRegister={onRegister}
    />
  );
}
